import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getBolDraft, upsertBolDraft, finalizeBol, voidBol, getBolPdfUrl,
  listBolsForShipment, evaluateBolRequirement, setShipmentBolFlags,
  getBolAuditTrail, listBolSignatures, recordBolSignature, reprintBol,
  recomputeBolTotals,
} from "@/lib/bol.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2, Save, FileCheck2, X, Download, Plus, Trash2, ArrowLeft, AlertTriangle, Printer,
  Calculator, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { SignaturePad } from "@/components/signature-pad";

export const Route = createFileRoute("/_authenticated/app/shipments/$shipmentId/bol")({
  component: BolEditorPage,
  errorComponent: BolRouteError,
  notFoundComponent: BolRouteNotFound,
});

function BolRouteError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  const { shipmentId } = Route.useParams();
  return (
    <BolErrorCard
      title="BOL failed to load"
      message={error?.message ?? "Unknown error"}
      shipmentId={shipmentId}
      onRetry={() => { router.invalidate(); reset(); }}
    />
  );
}

function BolRouteNotFound() {
  const { shipmentId } = Route.useParams();
  return (
    <BolErrorCard
      title="BOL not found"
      message="This shipment does not have an accessible Bill of Lading."
      shipmentId={shipmentId}
    />
  );
}

type Party = { name?: string; address?: string; contact?: string; phone?: string };
type Carrier = { name?: string; scac?: string; pro?: string; trailer?: string; driver?: string; seal?: string };
type Refs = { pos?: string[]; customer_refs?: string[]; order_numbers?: string[]; notes?: string };
type Commodity = { qty?: number | string; pkg_type?: string; weight_lb?: number | string; nmfc?: string; freight_class?: string; description?: string; hazmat?: boolean };
type Totals = { total_weight_lb?: number; total_pallets?: number; total_cartons?: number };

function BolEditorPage() {
  const { shipmentId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const draftFn = useServerFn(getBolDraft);
  const saveFn = useServerFn(upsertBolDraft);
  const finalizeFn = useServerFn(finalizeBol);
  const voidFn = useServerFn(voidBol);
  const pdfFn = useServerFn(getBolPdfUrl);
  const listFn = useServerFn(listBolsForShipment);
  const evalFn = useServerFn(evaluateBolRequirement);
  const flagFn = useServerFn(setShipmentBolFlags);
  const auditFn = useServerFn(getBolAuditTrail);
  const sigsFn = useServerFn(listBolSignatures);
  const recordSigFn = useServerFn(recordBolSignature);
  const reprintFn = useServerFn(reprintBol);
  const recomputeFn = useServerFn(recomputeBolTotals);

  const { data: evalRes } = useQuery({
    queryKey: ["bol-eval", shipmentId],
    queryFn: () => evalFn({ data: { shipmentId } }),
  });

  const { data: draftRes, isLoading, error: draftError, refetch: refetchDraft } = useQuery({
    queryKey: ["bol-draft", shipmentId],
    queryFn: () => draftFn({ data: { shipmentId } }),
    retry: 1,
  });

  const { data: listRes } = useQuery({
    queryKey: ["bol-list", shipmentId],
    queryFn: () => listFn({ data: { shipmentId } }),
  });

  const { data: auditRes } = useQuery({
    queryKey: ["bol-audit", (draftRes?.bol as any)?.id],
    queryFn: () => auditFn({ data: { bolId: (draftRes?.bol as any).id } }),
    enabled: !!(draftRes?.bol as any)?.id,
  });

  const { data: sigsRes } = useQuery({
    queryKey: ["bol-sigs", (draftRes?.bol as any)?.id],
    queryFn: () => sigsFn({ data: { bolId: (draftRes?.bol as any).id } }),
    enabled: !!(draftRes?.bol as any)?.id,
  });

  const bol = draftRes?.bol as any;
  const isDraft = bol?.status === "draft";

  // Local edit state
  const [shipper, setShipper] = useState<Party>({});
  const [consignee, setConsignee] = useState<Party>({});
  const [carrier, setCarrier] = useState<Carrier>({});
  const [refs, setRefs] = useState<Refs>({});
  const [commodities, setCommodities] = useState<Commodity[]>([]);
  const [totals, setTotals] = useState<Totals>({});
  const [special, setSpecial] = useState("");
  const [busy, setBusy] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");

  useEffect(() => {
    if (!bol) return;
    setShipper(bol.shipper ?? {});
    setConsignee(bol.consignee ?? {});
    setCarrier(bol.carrier_info ?? {});
    setRefs({
      pos: bol.ref_numbers?.pos ?? [],
      customer_refs: bol.ref_numbers?.customer_refs ?? [],
      order_numbers: bol.ref_numbers?.order_numbers ?? [],
      notes: bol.ref_numbers?.notes ?? "",
    });
    setCommodities(bol.commodity_lines ?? []);
    setTotals(bol.totals ?? {});
    setSpecial(bol.special_instructions ?? "");
  }, [bol?.id, bol?.revision]);

  useEffect(() => { document.title = `BOL editor · ${shipmentId.slice(0, 8)}`; }, [shipmentId]);

  const computedWeight = useMemo(
    () => commodities.reduce((s, c) => s + (Number(c.weight_lb) || 0), 0),
    [commodities],
  );

  const onSave = async () => {
    if (!bol) return;
    setBusy(true);
    try {
      await saveFn({ data: {
        bolId: bol.id,
        patch: {
          shipper, consignee, carrier_info: carrier,
          ref_numbers: refs,
          commodity_lines: commodities,
          totals,
          special_instructions: special,
        },
      }});
      toast.success("Draft saved");
      qc.invalidateQueries({ queryKey: ["bol-draft", shipmentId] });
      qc.invalidateQueries({ queryKey: ["bol-list", shipmentId] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setBusy(false); }
  };

  const onFinalize = async () => {
    if (!bol) return;
    setBusy(true);
    try {
      await saveFn({ data: { bolId: bol.id, patch: {
        shipper, consignee, carrier_info: carrier,
        ref_numbers: refs, commodity_lines: commodities, totals,
        special_instructions: special,
      } } });
      const res = await finalizeFn({ data: { bolId: bol.id } });
      toast.success(`BOL ${(res.bol as any).bol_number} finalized`);
      qc.invalidateQueries({ queryKey: ["bol-draft", shipmentId] });
      qc.invalidateQueries({ queryKey: ["bol-list", shipmentId] });
      qc.invalidateQueries({ queryKey: ["shipment", shipmentId] });
    } catch (e) {
      toast.error((e as Error).message, { duration: 8000 });
    } finally { setBusy(false); }
  };

  const onDownload = async () => {
    if (!bol) return;
    try {
      const { url } = await pdfFn({ data: { bolId: bol.id } });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error((e as Error).message); }
  };

  const onRecompute = async () => {
    try {
      const res = await recomputeFn({ data: { shipmentId } });
      const t = res.totals;
      setTotals({
        total_weight_lb: t.total_weight_lb,
        total_pallets: t.total_pallets,
        total_cartons: t.total_cartons,
      });
      toast.success("Totals recalculated from packed inventory");
    } catch (e) { toast.error((e as Error).message); }
  };

  const onReprint = async (regenerate: boolean) => {
    if (!bol) return;
    setBusy(true);
    try {
      const res = await reprintFn({ data: { bolId: bol.id, regenerate, copies: 1 } });
      toast.success(`${regenerate ? "BOL regenerated & " : ""}reprint queued (${res.copies} copy)`);
      // Also surface the latest PDF in a tab for hand-off
      try {
        const { url } = await pdfFn({ data: { bolId: bol.id } });
        window.open(url, "_blank", "noopener,noreferrer");
      } catch { /* non-fatal */ }
      qc.invalidateQueries({ queryKey: ["bol-draft", shipmentId] });
      qc.invalidateQueries({ queryKey: ["bol-audit", bol.id] });
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  const onSign = async (role: "shipper" | "driver" | "consignee", payload: { signer_name: string; signature_png: string }) => {
    if (!bol) return;
    try {
      await recordSigFn({ data: { bolId: bol.id, role, ...payload } });
      toast.success(`${role} signature captured`);
      qc.invalidateQueries({ queryKey: ["bol-sigs", bol.id] });
      qc.invalidateQueries({ queryKey: ["bol-audit", bol.id] });
    } catch (e) { toast.error((e as Error).message); }
  };

  const onVoid = async () => {
    if (!bol || voidReason.trim().length < 2) return;
    setBusy(true);
    try {
      await voidFn({ data: { bolId: bol.id, reason: voidReason.trim() } });
      toast.success("BOL voided");
      setVoidOpen(false); setVoidReason("");
      qc.invalidateQueries({ queryKey: ["bol-draft", shipmentId] });
      qc.invalidateQueries({ queryKey: ["bol-list", shipmentId] });
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  const onFreightMode = async (mode: string) => {
    try {
      await flagFn({ data: { shipmentId, freight_mode: mode as any } });
      qc.invalidateQueries({ queryKey: ["bol-eval", shipmentId] });
      qc.invalidateQueries({ queryKey: ["shipment", shipmentId] });
      toast.success(`Freight mode set to ${mode.toUpperCase()}`);
    } catch (e) { toast.error((e as Error).message); }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />Loading BOL…
      </div>
    );
  }
  if (draftError) {
    return (
      <BolErrorCard
        title="BOL failed to load"
        message={(draftError as Error)?.message ?? "Unknown error"}
        shipmentId={shipmentId}
        onRetry={() => refetchDraft()}
      />
    );
  }
  if (!bol) {
    return (
      <BolErrorCard
        title="No BOL available"
        message="A draft BOL could not be initialized for this shipment."
        shipmentId={shipmentId}
        onRetry={() => refetchDraft()}
      />
    );
  }

  const readOnly = !isDraft;

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/app/shipments/$shipmentId", params: { shipmentId } })}>
            <ArrowLeft className="h-4 w-4 mr-2" />Back to shipment
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight mt-2">
            Bill of Lading {bol.bol_number ? <span className="font-mono text-base text-muted-foreground">· {bol.bol_number}</span> : <span className="text-muted-foreground text-base">(draft)</span>}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Revision {bol.revision} · {evalRes?.freight_mode?.toUpperCase() ?? "—"}{evalRes?.has_routing_guide ? " · Customer routing guide applies" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill tone={statusTone(bol.status)} dot>{bol.status}</StatusPill>
        </div>
      </div>

      {evalRes && evalRes.reasons.length > 0 && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="py-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
            <div>
              <div className="font-medium">BOL required</div>
              <ul className="text-xs text-muted-foreground list-disc list-inside">
                {evalRes.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Freight Mode</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 max-w-sm">
            <Select value={evalRes?.freight_mode ?? "parcel"} onValueChange={onFreightMode} disabled={readOnly}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="parcel">Parcel</SelectItem>
                <SelectItem value="ltl">LTL</SelectItem>
                <SelectItem value="partial_tl">Partial truckload</SelectItem>
                <SelectItem value="ftl">Full truckload</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <PartyCard title="Shipper" value={shipper} onChange={setShipper} disabled={readOnly} />
        <PartyCard title="Consignee" value={consignee} onChange={setConsignee} disabled={readOnly} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Carrier</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Carrier name" value={carrier.name} onChange={(v) => setCarrier({ ...carrier, name: v })} disabled={readOnly} />
          <Field label="SCAC" value={carrier.scac} onChange={(v) => setCarrier({ ...carrier, scac: v?.toUpperCase() })} disabled={readOnly} />
          <Field label="PRO #" value={carrier.pro} onChange={(v) => setCarrier({ ...carrier, pro: v })} disabled={readOnly} />
          <Field label="Trailer #" value={carrier.trailer} onChange={(v) => setCarrier({ ...carrier, trailer: v })} disabled={readOnly} />
          <Field label="Driver" value={carrier.driver} onChange={(v) => setCarrier({ ...carrier, driver: v })} disabled={readOnly} />
          <Field label="Seal #" value={carrier.seal} onChange={(v) => setCarrier({ ...carrier, seal: v })} disabled={readOnly} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Reference Numbers</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-3">
          <ListField label="PO numbers" value={refs.pos ?? []} onChange={(v) => setRefs({ ...refs, pos: v })} disabled={readOnly} />
          <ListField label="Customer refs" value={refs.customer_refs ?? []} onChange={(v) => setRefs({ ...refs, customer_refs: v })} disabled={readOnly} />
          <ListField label="Order numbers" value={refs.order_numbers ?? []} onChange={(v) => setRefs({ ...refs, order_numbers: v })} disabled={readOnly} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Commodity Lines</CardTitle>
          {!readOnly && (
            <Button size="sm" variant="outline" onClick={() => setCommodities([...commodities, { qty: 1, pkg_type: "Pallet", weight_lb: 0, description: "" }])}>
              <Plus className="h-4 w-4 mr-1" />Add line
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {commodities.length === 0 && <p className="text-sm text-muted-foreground">No commodity lines.</p>}
          {commodities.map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end border-b pb-2">
              <div className="col-span-1"><Label className="text-[10px] uppercase">Qty</Label><Input value={c.qty ?? ""} onChange={(e) => updateAt(commodities, setCommodities, i, { qty: e.target.value })} disabled={readOnly} /></div>
              <div className="col-span-2"><Label className="text-[10px] uppercase">Pkg type</Label><Input value={c.pkg_type ?? ""} onChange={(e) => updateAt(commodities, setCommodities, i, { pkg_type: e.target.value })} disabled={readOnly} /></div>
              <div className="col-span-2"><Label className="text-[10px] uppercase">Weight (lb)</Label><Input type="number" value={c.weight_lb ?? ""} onChange={(e) => updateAt(commodities, setCommodities, i, { weight_lb: e.target.value })} disabled={readOnly} /></div>
              <div className="col-span-2"><Label className="text-[10px] uppercase">NMFC</Label><Input value={c.nmfc ?? ""} onChange={(e) => updateAt(commodities, setCommodities, i, { nmfc: e.target.value })} disabled={readOnly} /></div>
              <div className="col-span-1"><Label className="text-[10px] uppercase">Class</Label><Input value={c.freight_class ?? ""} onChange={(e) => updateAt(commodities, setCommodities, i, { freight_class: e.target.value })} disabled={readOnly} /></div>
              <div className="col-span-3"><Label className="text-[10px] uppercase">Description</Label><Input value={c.description ?? ""} onChange={(e) => updateAt(commodities, setCommodities, i, { description: e.target.value })} disabled={readOnly} /></div>
              <div className="col-span-1 flex justify-end">
                {!readOnly && <Button variant="ghost" size="icon" onClick={() => setCommodities(commodities.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground pt-2">Sum of line weights: <span className="font-mono">{computedWeight.toFixed(2)} lb</span></p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Totals</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <NumField label="Total weight (lb)" value={totals.total_weight_lb} onChange={(v) => setTotals({ ...totals, total_weight_lb: v })} disabled={readOnly} />
            <NumField label="Total pallets" value={totals.total_pallets} onChange={(v) => setTotals({ ...totals, total_pallets: v })} disabled={readOnly} />
            <NumField label="Total cartons" value={totals.total_cartons} onChange={(v) => setTotals({ ...totals, total_cartons: v })} disabled={readOnly} />
          </div>
          {!readOnly && (
            <Button size="sm" variant="outline" onClick={onRecompute}>
              <Calculator className="h-3.5 w-3.5 mr-2" />Auto-calculate from packed inventory
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Special Instructions</CardTitle></CardHeader>
        <CardContent>
          <Textarea value={special} onChange={(e) => setSpecial(e.target.value)} disabled={readOnly} rows={3} placeholder="Lift-gate, residential, appointment required…" />
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 pt-2 sticky bottom-0 bg-background py-3 border-t">
        {isDraft && (
          <>
            <Button onClick={onSave} disabled={busy} variant="outline">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save draft
            </Button>
            <Button onClick={onFinalize} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileCheck2 className="h-4 w-4 mr-2" />}Finalize & generate PDF
            </Button>
          </>
        )}
        {bol.pdf_storage_path && (
          <Button onClick={onDownload} variant="outline"><Download className="h-4 w-4 mr-2" />Download PDF</Button>
        )}
        {bol.status === "finalized" && (
          <>
            <Button onClick={() => onReprint(false)} variant="outline" disabled={busy}>
              <Printer className="h-4 w-4 mr-2" />Reprint
            </Button>
            <Button onClick={() => onReprint(true)} variant="outline" disabled={busy}>
              <RefreshCw className="h-4 w-4 mr-2" />Regenerate & reprint
            </Button>
          </>
        )}
        <Button asChild variant="outline">
          <Link to="/app/shipments/$shipmentId/bol/print" params={{ shipmentId }} target="_blank">
            <Printer className="h-4 w-4 mr-2" />Print view
          </Link>
        </Button>
        {bol.status === "finalized" && (
          <Button onClick={() => setVoidOpen(true)} variant="ghost" className="ml-auto text-destructive"><X className="h-4 w-4 mr-2" />Void BOL</Button>
        )}
      </div>

      {bol.status === "finalized" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Signatures</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-4">
            {(["shipper", "driver", "consignee"] as const).map((role) => {
              const existing = sigsRes?.signatures.find((s: any) => s.role === role);
              return (
                <div key={role} className="space-y-2 border rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium uppercase tracking-wide">{role}</div>
                    {existing && (
                      <span className="text-[10px] text-muted-foreground tabular-nums">
                        {new Date(existing.signed_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {existing ? (
                    <>
                      <img src={existing.signature_png} alt={`${role} signature`} className="h-24 w-full object-contain bg-white border rounded" />
                      <div className="text-xs"><span className="text-muted-foreground">Signed by </span>{existing.signer_name}</div>
                      <SignaturePad role={role} defaultName={existing.signer_name} onSubmit={(p) => onSign(role, p)} />
                    </>
                  ) : (
                    <SignaturePad role={role} onSubmit={(p) => onSign(role, p)} />
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Audit Trail</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-xs">
          {!auditRes ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : auditRes.events.length === 0 ? (
            <div className="text-muted-foreground">No history yet.</div>
          ) : auditRes.events.map((e, i) => (
            <div key={i} className="grid grid-cols-[160px_120px_1fr] gap-3 py-1 border-b last:border-0">
              <div className="text-muted-foreground tabular-nums">{new Date(e.at).toLocaleString()}</div>
              <div className="font-medium truncate" title={e.actor}>{e.actor}</div>
              <div>{e.action}{e.detail ? <span className="text-muted-foreground"> — {e.detail}</span> : null}</div>
            </div>
          ))}
          {listRes && listRes.bols.length > 1 && (
            <div className="pt-3 mt-2 border-t">
              <div className="text-muted-foreground mb-1">Prior BOLs on this shipment:</div>
              {listRes.bols.map((b: any) => (
                <div key={b.id} className="grid grid-cols-[160px_120px_1fr] gap-3 py-1">
                  <div className="text-muted-foreground tabular-nums">{new Date(b.finalized_at ?? b.created_at).toLocaleString()}</div>
                  <div className="font-mono">{b.bol_number ?? "(draft)"}</div>
                  <div>{b.status} · rev {b.revision}{b.voided_reason ? ` — ${b.voided_reason}` : ""}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Void this BOL?</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Reason (required)</Label>
            <Textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)} rows={3} />
            <p className="text-xs text-muted-foreground">Voiding keeps the audit trail. You can create a new BOL afterwards.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVoidOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={onVoid} disabled={busy || voidReason.trim().length < 2}>Void BOL</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function updateAt<T>(arr: T[], setter: (v: T[]) => void, i: number, patch: Partial<T>) {
  setter(arr.map((row, idx) => idx === i ? { ...row, ...patch } : row));
}

function Field({ label, value, onChange, disabled }: { label: string; value?: string | null; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
    </div>
  );
}

function NumField({ label, value, onChange, disabled }: { label: string; value?: number; onChange: (v: number) => void; disabled?: boolean }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Input type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} disabled={disabled} />
    </div>
  );
}

function ListField({ label, value, onChange, disabled }: { label: string; value: string[]; onChange: (v: string[]) => void; disabled?: boolean }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Input value={(value ?? []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} disabled={disabled} placeholder="comma separated" />
    </div>
  );
}

function PartyCard({ title, value, onChange, disabled }: { title: string; value: Party; onChange: (v: Party) => void; disabled?: boolean }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <Field label="Name" value={value.name} onChange={(v) => onChange({ ...value, name: v })} disabled={disabled} />
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Address</Label>
          <Textarea rows={3} value={value.address ?? ""} onChange={(e) => onChange({ ...value, address: e.target.value })} disabled={disabled} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact" value={value.contact} onChange={(v) => onChange({ ...value, contact: v })} disabled={disabled} />
          <Field label="Phone" value={value.phone} onChange={(v) => onChange({ ...value, phone: v })} disabled={disabled} />
        </div>
      </CardContent>
    </Card>
  );
}

function BolErrorCard({
  title, message, shipmentId, onRetry,
}: { title: string; message: string; shipmentId: string; onRetry?: () => void }) {
  return (
    <div className="p-4 sm:p-6 max-w-2xl">
      <Card className="border-destructive/40 bg-destructive/5">
        <CardHeader className="flex flex-row items-start gap-2 space-y-0">
          <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1 break-words">{message}</p>
          </div>
        </CardHeader>
        <CardContent className="flex items-center gap-2">
          {onRetry && (
            <Button size="sm" onClick={onRetry}>
              <RefreshCw className="h-4 w-4 mr-2" />Retry
            </Button>
          )}
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/shipments/$shipmentId" params={{ shipmentId }}>
              <ArrowLeft className="h-4 w-4 mr-2" />Back to shipment
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}